/**
 * CD-8740 — "the cached profile changed behind the UI's back".
 *
 * The signed-in user lives in `AuthData` (localStorage), and the screens that
 * guard on it (`AuthProvider`, `withCurrentUser`) read it during render. That is
 * fine while the profile only changes through the sign-in / sign-out flows, which
 * navigate and so re-render anyway. It is not fine for the boot session probe's
 * **late** result — one that settles after `BOOT_PROBE_TIMEOUT_MS`, once the app
 * has painted — which stores or drops the profile with nothing to trigger a
 * render. The probe calls `notifyAuthChanged()` then, and those readers
 * re-render.
 *
 * Deliberately NOT emitted from `AuthData.store` / `drop`: sign-in would then
 * re-render the sign-in route mid-submit and redirect to `/dashboard` before the
 * form's own post-login navigation (its `?url=` target, the role's homepage) runs.
 *
 * Tab-local on purpose. Other tabs hear nothing; that would need the `storage`
 * event, and every hourly refresh would then redraw every tab.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Call `listener` whenever `notifyAuthChanged` fires. Returns the unsubscribe. */
export const subscribeToAuthChanges = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Tell every subscriber the cached profile changed outside a render. */
export const notifyAuthChanged = (): void => {
  // A copy, so a listener may unsubscribe (or subscribe) while we iterate; and
  // one listener throwing must not stop the rest being told.
  [...listeners].forEach((listener) => {
    try {
      listener();
    } catch (_e) {
      // Not ours to handle; the others still need to hear.
    }
  });
};
